import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["input", "submit", "resultContainer", "progressContainer", "progressBar", "progressMessage", "fileName"]

  connect() {
    if (this.element.tagName !== 'FORM') return

    if (this.data.get('status-url')) {
      this.element.addEventListener('submit', this.handleAsyncSubmit.bind(this))
    } else {
      this.element.addEventListener('ajax:success', this.handleSuccess.bind(this))
      this.element.addEventListener('ajax:error', this.handleError.bind(this))
    }
  }

  disconnect() {
    this._pollJobId = null
    this._isUploading = false
    if (this._pollTimer) clearTimeout(this._pollTimer)
  }

  toggleSubmitButton() {
    const file = this.inputTarget.files[0]

    if (!file) {
      this.submitTarget.disabled = true
      if (this.hasFileNameTarget) this.fileNameTarget.textContent = ''
      return
    }

    this.submitTarget.disabled = false
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = file.name
    if (this.hasResultContainerTarget) this.resultContainerTarget.innerHTML = ''
  }

  dragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.add('is-drag-over')
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.remove('is-drag-over')
  }

  dropFiles(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.classList.remove('is-drag-over')

    if (!event.dataTransfer || !event.dataTransfer.files || event.dataTransfer.files.length === 0) return

    const transfer = new DataTransfer()
    transfer.items.add(event.dataTransfer.files[0])
    this.inputTarget.files = transfer.files
    this.toggleSubmitButton()
  }

  openFilePicker(event) {
    event.preventDefault()
    if (this.hasInputTarget) this.inputTarget.click()
  }

  // ── Async mode (data-file-upload-status-url present) ─────────────────────────

  async handleAsyncSubmit(event) {
    event.preventDefault()
    if (this._isUploading) return

    const formData = new FormData(this.element)
    this._setUploadingState(true)
    this.submitTarget.disabled = true
    this._showProgress(5, '')

    try {
      const response = await fetch(this.element.action, {
        method: 'POST',
        body: formData,
        headers: { 'X-CSRF-Token': this._csrfToken() },
        credentials: 'same-origin'
      })

      const data = await response.json()

      if (!response.ok || !data.job_id) {
        this._showError(data.message || (data.errors && data.errors[0]) || 'Upload failed')
        return
      }

      this._startPolling(data.job_id)
    } catch (e) {
      this._showError(e.message)
    }
  }

  _startPolling(jobId) {
    this._pollJobId = jobId
    this._pollAttempts = 0
    this._schedulePoll(1000)
  }

  _schedulePoll(delay) {
    this._pollTimer = setTimeout(() => this._poll(), delay)
  }

  async _poll() {
    if (!this._pollJobId) return

    this._pollAttempts++
    if (this._pollAttempts > 120) {
      this._showError('Import timed out. Please try again.')
      return
    }

    try {
      const statusUrlTemplate = this.data.get('status-url')
      const statusUrl = statusUrlTemplate.replace('__JOB_ID__', encodeURIComponent(this._pollJobId))
      const response = await fetch(statusUrl, {
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': this._csrfToken() },
        credentials: 'same-origin'
      })

      const data = await response.json()

      switch (data.state) {
        case 'complete':
          this._showProgress(100, data.message)
          setTimeout(() => this._onComplete(data), 400)
          break
        case 'failed':
          this._showError(data.message || 'Import failed. Please try again.')
          break
        case 'expired':
        case 'not_found':
          this._showError(data.message || 'Job not found. Please try again.')
          break
        default:
          this._showProgress(data.progress || 10, data.message)
          this._schedulePoll(1000)
      }
    } catch (_e) {
      this._schedulePoll(2000)
    }
  }

  _onComplete(data) {
    this._hideProgress()
    if (this.hasResultContainerTarget) {
      let html = `<div class="alert alert-success mt-4">${data.message}`

      if (data.download) {
        const url = this.downloadUrl(data.download)
        const label = this.data.get('download-label') || 'Download result file'
        html += `<div class="mt-2"><a href="${url}" download="${data.download.filename}" class="alert-link bulk-accounting-control__download-link" data-action="click->file-upload#revokeDownloadUrl">${label}</a></div>`
      }

      html += '</div>'
      this.resultContainerTarget.innerHTML = html
    }
    this._resetInput()
  }

  _showProgress(percent, message) {
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.remove('d-none')
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
      this.progressBarTarget.setAttribute('aria-valuenow', percent)
    }
    if (this.hasProgressMessageTarget) this.progressMessageTarget.textContent = message || ''
    if (this.hasResultContainerTarget) this.resultContainerTarget.innerHTML = ''
  }

  _hideProgress() {
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.add('d-none')
  }

  _showError(message) {
    this._hideProgress()
    if (this.hasResultContainerTarget) {
      this.resultContainerTarget.innerHTML = `<div class="alert alert-danger mt-4">${message}</div>`
    }
    this._resetInput()
  }

  _resetInput() {
    this.inputTarget.value = ''
    this._setUploadingState(false)
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = ''
    requestAnimationFrame(() => { this.submitTarget.disabled = true })
    this._pollJobId = null
  }

  // ── Sync mode (legacy Rails UJS ajax:success / ajax:error) ───────────────────

  handleSuccess(event) {
    if (!this.hasResultContainerTarget) return

    this._setUploadingState(false)

    const [data] = event.detail

    if (data.success) {
      this.resultContainerTarget.innerHTML = `<div class="alert alert-success mt-4">${data.message}</div>`

      if (data.download) {
        this.downloadFile(data.download)
      }
    } else {
      let errorHTML = `<div class="alert alert-danger mt-4">${data.message}<ul>`
      data.errors.forEach(error => {
        errorHTML += `<li>${error}</li>`
      })
      errorHTML += '</ul></div>'
      this.resultContainerTarget.innerHTML = errorHTML
    }

    this.inputTarget.value = ''

    requestAnimationFrame(() => {
      this.submitTarget.disabled = true
    })
  }

  handleError(event) {
    const [data] = event.detail

    this._setUploadingState(false)

    if (this.hasResultContainerTarget) {
      this.resultContainerTarget.innerHTML = `<div class="alert alert-danger mt-4">${data.message}</div>`
    }
    this.toggleSubmitButton()
  }

  // ── Shared ────────────────────────────────────────────────────────────────────

  downloadUrl(download) {
    const byteCharacters = atob(download.data)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], { type: download.content_type })
    return window.URL.createObjectURL(blob)
  }

  revokeDownloadUrl(event) {
    const url = event.currentTarget.getAttribute('href')
    if (!url) return

    setTimeout(() => {
      window.URL.revokeObjectURL(url)
    }, 2000)
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  _setUploadingState(isUploading) {
    this._isUploading = isUploading
    if (this.hasInputTarget) this.inputTarget.disabled = isUploading

    const uploadPanel = this.hasInputTarget
      ? this.inputTarget.closest('.bulk-accounting-control__panel--upload')
      : null

    if (uploadPanel) uploadPanel.classList.toggle('is-uploading', isUploading)
  }
}
